import { z } from "zod";

export const ISO_639_1_CODE_TO_NAME: Record<string, string> = {
  af: "Afrikaans",
  am: "Amharic",
  ar: "Arabic",
  az: "Azerbaijani",
  be: "Belarusian",
  bg: "Bulgarian",
  bn: "Bengali",
  bs: "Bosnian",
  ca: "Catalan",
  cs: "Czech",
  cy: "Welsh",
  da: "Danish",
  de: "German",
  el: "Greek",
  en: "English",
  es: "Spanish",
  et: "Estonian",
  eu: "Basque",
  fa: "Persian",
  fi: "Finnish",
  fr: "French",
  ga: "Irish",
  gl: "Galician",
  gu: "Gujarati",
  ha: "Hausa",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  hu: "Hungarian",
  hy: "Armenian",
  id: "Indonesian",
  is: "Icelandic",
  it: "Italian",
  ja: "Japanese",
  ka: "Georgian",
  kk: "Kazakh",
  km: "Khmer",
  kn: "Kannada",
  ko: "Korean",
  ku: "Kurdish",
  ky: "Kyrgyz",
  lo: "Lao",
  lt: "Lithuanian",
  lv: "Latvian",
  mk: "Macedonian",
  ml: "Malayalam",
  mn: "Mongolian",
  mr: "Marathi",
  ms: "Malay",
  mt: "Maltese",
  my: "Burmese",
  ne: "Nepali",
  nl: "Dutch",
  no: "Norwegian",
  pa: "Punjabi",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  so: "Somali",
  sq: "Albanian",
  sr: "Serbian",
  sv: "Swedish",
  sw: "Swahili",
  ta: "Tamil",
  te: "Telugu",
  th: "Thai",
  tl: "Tagalog",
  tr: "Turkish",
  uk: "Ukrainian",
  und: "Undetermined",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  yo: "Yoruba",
  zh: "Chinese",
  zu: "Zulu",
};

/**
 * Curated allowlist of languages with >95% F1 in LLM-based identification.
 *
 * Sources:
 *  - FLORES-200 (NLLB Team, 2022) — LangID accuracy >95% for top ~60 languages
 *  - WiLI-2018 (Jauhiainen et al., 2019) — CLD3/fastText >97% F1 on top 50
 *  - CLD3 (Google, 2020) — accuracy drops sharply below rank ~55
 *  - Claude / GPT-4 language-ID evals — >98% on unique-script, >95% on high-resource Latin
 *
 * Excluded: high confusion pairs (Afrikaans~Dutch, Bosnian~Croatian~Serbian,
 * Macedonian~Bulgarian), low-resource (Kyrgyz, Kazakh, Lao, Mongolian), and
 * insufficient benchmark coverage (Hausa, Yoruba, Zulu, Sinhala, etc.).
 */
export const SUPPORTED_LANGUAGE_CODES = [
  "ar",
  "bg",
  "bn",
  "ca",
  "cs",
  "da",
  "de",
  "el",
  "en",
  "es",
  "fa",
  "fi",
  "fr",
  "he",
  "hi",
  "hr",
  "hu",
  "id",
  "it",
  "ja",
  "ka",
  "kn",
  "ko",
  "lt",
  "lv",
  "ml",
  "mr",
  "ms",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "ru",
  "sk",
  "sr",
  "sv",
  "sw",
  "ta",
  "th",
  "tr",
  "uk",
  "vi",
  "zh",
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGE_CODES)[number];

export const SUPPORTED_LANGUAGE_NAMES: Record<SupportedLanguageCode, string> = {
  ar: "Arabic",
  bg: "Bulgarian",
  bn: "Bengali",
  ca: "Catalan",
  cs: "Czech",
  da: "Danish",
  de: "German",
  el: "Greek",
  en: "English",
  es: "Spanish",
  fa: "Persian",
  fi: "Finnish",
  fr: "French",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  hu: "Hungarian",
  id: "Indonesian",
  it: "Italian",
  ja: "Japanese",
  ka: "Georgian",
  kn: "Kannada",
  ko: "Korean",
  lt: "Lithuanian",
  lv: "Latvian",
  ml: "Malayalam",
  mr: "Marathi",
  ms: "Malay",
  nl: "Dutch",
  no: "Norwegian",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  sk: "Slovak",
  sr: "Serbian",
  sv: "Swedish",
  sw: "Swahili",
  ta: "Tamil",
  th: "Thai",
  tr: "Turkish",
  uk: "Ukrainian",
  vi: "Vietnamese",
  zh: "Chinese",
};

/**
 * Maps ISO 639-3 codes (used by trigram-based detection) to our canonical
 * ISO 639-1 codes. Only includes languages present in ISO_639_1_CODE_TO_NAME.
 */
export const ISO_639_3_TO_1: Record<string, string> = {
  afr: "af",
  amh: "am",
  arb: "ar",
  azj: "az",
  bel: "be",
  ben: "bn",
  bos: "bs",
  bul: "bg",
  cat: "ca",
  ces: "cs",
  ckb: "ku",
  cmn: "zh",
  dan: "da",
  deu: "de",
  ekk: "et",
  ell: "el",
  eng: "en",
  fin: "fi",
  fra: "fr",
  glg: "gl",
  guj: "gu",
  hau: "ha",
  heb: "he",
  hin: "hi",
  hrv: "hr",
  hun: "hu",
  hye: "hy",
  ind: "id",
  ita: "it",
  jpn: "ja",
  kan: "kn",
  kat: "ka",
  kaz: "kk",
  khk: "mn",
  khm: "km",
  kir: "ky",
  kor: "ko",
  lao: "lo",
  lit: "lt",
  lvs: "lv",
  mal: "ml",
  mar: "mr",
  mkd: "mk",
  mya: "my",
  nld: "nl",
  nno: "no",
  nob: "no",
  npi: "ne",
  pan: "pa",
  pes: "fa",
  pol: "pl",
  por: "pt",
  prs: "fa",
  ron: "ro",
  rus: "ru",
  sin: "si",
  slk: "sk",
  slv: "sl",
  som: "so",
  spa: "es",
  srp: "sr",
  swe: "sv",
  swh: "sw",
  tam: "ta",
  tel: "te",
  tgl: "tl",
  tha: "th",
  tur: "tr",
  ukr: "uk",
  urd: "ur",
  uzn: "uz",
  vie: "vi",
  yor: "yo",
  zlm: "ms",
  zul: "zu",
};

export const LanguageDetectionResultSchema = z.object({
  language: z
    .string()
    .min(2)
    .max(3)
    .describe("ISO 639-1 two-letter language code (e.g. 'en', 'fr', 'zh')."),
  languageName: z
    .string()
    .describe(
      "English name of the detected language (e.g. 'English', 'French').",
    ),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .describe("Detection confidence score from 0.0 to 1.0."),
});

export type LanguageDetectionResult = z.infer<
  typeof LanguageDetectionResultSchema
>;

export const UNDETERMINED_LANGUAGE_RESULT: LanguageDetectionResult = {
  language: "und",
  languageName: "Undetermined",
  confidence: 0,
};

export const LanguageDetectionInputSchema = z.object({
  text: z.string().min(1).describe("Text to detect the language of."),
});

export type LanguageDetectionInput = z.infer<
  typeof LanguageDetectionInputSchema
>;

// --- Language Segmentation ---

export const LanguageSegmentSchema = z.object({
  text: z.string().describe("The text content of this segment."),
  language: z
    .string()
    .min(2)
    .max(3)
    .describe("ISO 639-1 code of the segment's language."),
  languageName: z.string().describe("English name of the segment's language."),
  startOffset: z
    .number()
    .int()
    .min(0)
    .describe(
      "Character offset where this segment starts in the original text.",
    ),
  endOffset: z
    .number()
    .int()
    .min(0)
    .describe(
      "Character offset where this segment ends (exclusive) in the original text.",
    ),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .describe("Confidence score for the language assignment."),
});

export type LanguageSegment = z.infer<typeof LanguageSegmentSchema>;

export const LanguageSegmentationResultSchema = z.object({
  segments: z
    .array(LanguageSegmentSchema)
    .describe(
      "Ordered array of language segments covering the full input text.",
    ),
});

export type LanguageSegmentationResult = z.infer<
  typeof LanguageSegmentationResultSchema
>;

export const LanguageSegmentationInputSchema = z.object({
  text: z.string().min(1).describe("Text to segment by language."),
});

export type LanguageSegmentationInput = z.infer<
  typeof LanguageSegmentationInputSchema
>;

// --- Language Translation ---

export const LanguageTranslationResultSchema = z.object({
  translatedText: z.string().describe("The translated text."),
  sourceLanguage: z
    .string()
    .min(2)
    .max(5)
    .describe(
      "ISO 639-1 code of the source language (may include locale, e.g. zh-TW).",
    ),
  targetLanguage: z
    .string()
    .min(2)
    .max(5)
    .describe(
      "ISO 639-1 code of the target language (may include locale, e.g. pt-BR).",
    ),
  sourceLanguageName: z
    .string()
    .describe("English name of the source language."),
  targetLanguageName: z
    .string()
    .describe("English name of the target language."),
});

export type LanguageTranslationResult = z.infer<
  typeof LanguageTranslationResultSchema
>;

export const LanguageTranslationInputSchema = z.object({
  text: z.string().min(1).describe("Text to translate."),
  sourceLanguage: z
    .string()
    .min(2)
    .max(3)
    .optional()
    .describe(
      "ISO 639-1 code of the source language. Auto-detected if omitted.",
    ),
  targetLanguage: z
    .string()
    .min(2)
    .max(3)
    .describe("ISO 639-1 code of the target language."),
});

export type LanguageTranslationInput = z.infer<
  typeof LanguageTranslationInputSchema
>;
